/* =============================================
   Hero Slideshow — hero-slideshow.js
   Web component: autoplay, dots, arrows, swipe,
   Shopify theme editor support.

   TRUE INFINITE LOOP (slide transition mode):
   We clone the first slide and append it after the
   last real slide, and clone the last slide and
   prepend it before the first real slide.
   Internal index space: [clone-of-last, real 0, real 1, ..., real N-1, clone-of-first]
   i.e. real slide i lives at internal index (i + 1).

   Going "next" past the last real slide slides the
   track visually into the clone-of-first (looks
   identical to slide 0). The instant that transition
   finishes, we silently teleport (transition: none)
   back to the *real* slide 0 at the matching position.
   The viewer never sees a jump.

   NAVIGATION LOCK: a CSS transition can be
   interrupted/retargeted (e.g. clicking "next" again
   before the previous transition finishes). When that
   happens the browser cancels the in-flight transition
   and its `transitionend` event never fires. If our
   loop-reset logic depended solely on that event, a
   dropped event would leave the internal index
   climbing unbounded — visually the track scrolls
   miles past all the real content into blank space.
   To prevent that we lock navigation: a new slide
   change is ignored until the current one (transition
   + any loop-reset) has actually finished, with a
   timeout fallback in case the event never fires at
   all (e.g. prefers-reduced-motion disables the
   transition entirely, so there's nothing to end).

   Fade transition doesn't need any of this: it's a
   pure opacity crossfade with no moving position, so
   there's nothing that can drift.
   ============================================= */

if (!customElements.get('hero-slideshow')) {
  class HeroSlideshow extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-track]');

      const realSlides = Array.from(this.querySelectorAll('[data-slide]'));
      this.total = realSlides.length;
      this.isFade = this.dataset.transition === 'fade';

      // Only loop-clone for the sliding transition, and only if there's
      // more than one slide (nothing to loop with just one).
      this.isLooping = !this.isFade && this.total > 1;

      if (this.isLooping) {
        this.setupLoopClones(realSlides);
      }

      // Re-query so `this.slides` includes the clones (if any).
      this.slides = Array.from(this.querySelectorAll('[data-slide]'));
      this.dots = Array.from(this.querySelectorAll('[data-dot]'));
      this.prevBtn = this.querySelector('[data-prev]');
      this.nextBtn = this.querySelector('[data-next]');

      // Internal index: for looping mode, real slide 0 sits at index 1
      // (because index 0 is the prepended clone-of-last).
      this.index = this.isLooping ? 1 : 0;
      this.isAnimating = false;
      this.navSafetyTimer = null;

      this.autoplayEnabled = this.dataset.autoplay === 'true' && this.total > 1;
      this.speed = (parseInt(this.dataset.speed, 10) || 5) * 1000;
      this.timer = null;

      if (this.total <= 1) return;

      this.bindControls();
      this.bindSwipe();
      this.bindAutoplayPausing();
      this.bindEditorEvents();
      this.bindTransitionEnd();

      // Position the track at the starting slide with no transition
      // (avoids a visible slide-in on page load).
      this.setPositionInstant(this.index);

      this.startAutoplay();
    }

    disconnectedCallback() {
      this.stopAutoplay();
      clearTimeout(this.navSafetyTimer);
    }

    /* ---------- Loop clone setup ---------- */

    setupLoopClones(realSlides) {
      const first = realSlides[0];
      const last = realSlides[realSlides.length - 1];

      const makeClone = (node) => {
        const clone = node.cloneNode(true);
        clone.setAttribute('aria-hidden', 'true');
        clone.setAttribute('data-clone', 'true');
        clone.removeAttribute('id');
        // Prevent clones from being reachable via Tab / being announced.
        clone
          .querySelectorAll('a, button, [tabindex]')
          .forEach((el) => el.setAttribute('tabindex', '-1'));

        // The original slide this was cloned from may have been
        // loading="lazy" (only the very first real slide is eager).
        // A clone sits right at the edge of the track from the start,
        // so force it to load right away — otherwise looping to it can
        // show a blank/white flash while the browser fetches it.
        clone.querySelectorAll('img').forEach((img) => {
          img.setAttribute('loading', 'eager');
          img.setAttribute('fetchpriority', 'high');
        });

        return clone;
      };

      const firstClone = makeClone(first);
      const lastClone = makeClone(last);

      this.track.insertBefore(lastClone, this.track.firstChild);
      this.track.appendChild(firstClone);
    }

    /* ---------- Navigation (locked, drift-proof) ---------- */

    /* All slide-changing requests (next/prev/dots) funnel through here.
       For the sliding transition it refuses to start a new move while
       one is still in flight, so the internal index can never run away. */
    navigate(targetIndex, userInitiated = false) {
      if (this.isFade) {
        this.goTo(targetIndex, userInitiated);
        return;
      }

      if (this.isAnimating || targetIndex === this.index) return;

      this.isAnimating = true;
      this.goTo(targetIndex, userInitiated);

      // Fallback in case transitionend never fires (reduced-motion,
      // an interrupted/cancelled transition, browser quirks, etc.)
      clearTimeout(this.navSafetyTimer);
      this.navSafetyTimer = setTimeout(() => this.finishSlideNavigation(), 800);
    }

    /* Runs once per completed (or safety-timed-out) slide change:
       does the invisible loop-reset teleport if we landed on a clone,
       then unlocks navigation. Safe to call more than once. */
    finishSlideNavigation() {
      if (!this.isAnimating) return;
      clearTimeout(this.navSafetyTimer);

      if (this.isLooping) {
        if (this.index === 0) {
          this.setPositionInstant(this.total);
        } else if (this.index === this.total + 1) {
          this.setPositionInstant(1);
        }
      }

      this.isAnimating = false;
    }

    bindTransitionEnd() {
      this.track.addEventListener('transitionend', (e) => {
        if (e.target !== this.track) return; // ignore bubbled child transitions
        if (e.propertyName && e.propertyName !== 'transform') return;
        this.finishSlideNavigation();
      });
    }

    /* ---------- Core position/state update (no locking here) ---------- */

    goTo(i, userInitiated = false) {
      if (this.isLooping) {
        // Raw index — allowed to temporarily land on the clone slots
        // (0 and total+1). finishSlideNavigation() silently corrects it.
        this.index = i;
      } else {
        this.index = ((i % this.total) + this.total) % this.total;
      }

      this.render();

      if (userInitiated) this.restartAutoplay();
    }

    /* Jump to an index with no transition (initial paint, and the
       invisible loop-reset teleport). */
    setPositionInstant(i) {
      this.index = i;
      const prevTransition = this.track.style.transition;
      this.track.style.transition = 'none';
      this.render();
      // Force reflow so the "none" transition is actually applied
      // before we restore it.
      void this.track.offsetHeight;
      this.track.style.transition = prevTransition;
    }

    render() {
      this.slides.forEach((slide, idx) => {
        slide.classList.toggle('is-active', idx === this.index);
      });

      if (!this.isFade) {
        this.track.style.transform = `translateX(-${this.index * 100}%)`;
      }

      const realIndex = this.realIndexOf(this.index);
      this.dots.forEach((dot, idx) => {
        const active = idx === realIndex;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-selected', active ? 'true' : 'false');
      });
    }

    /* Maps an internal index (which may point at a clone) to the
       real 0-based slide index it visually represents. */
    realIndexOf(i) {
      if (!this.isLooping) return i;
      return ((i - 1) % this.total + this.total) % this.total;
    }

    /* Converts a real 0-based slide index (e.g. from a dot) into the
       internal index space. */
    toInternalIndex(realIndex) {
      return this.isLooping ? realIndex + 1 : realIndex;
    }

    next(userInitiated = false) {
      this.navigate(this.index + 1, userInitiated);
    }

    prev(userInitiated = false) {
      this.navigate(this.index - 1, userInitiated);
    }

    /* ---------- Controls ---------- */

    bindControls() {
      this.dots.forEach((dot) => {
        dot.addEventListener('click', () => {
          const realIndex = parseInt(dot.dataset.index, 10);
          this.navigate(this.toInternalIndex(realIndex), true);
        });
      });

      if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.prev(true));
      if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.next(true));

      this.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowLeft') this.prev(true);
        if (e.key === 'ArrowRight') this.next(true);
      });
    }

    /* ---------- Swipe / drag (mobile touch + desktop mouse) ---------- */

    bindSwipe() {
      this.dragDelta = 0;
      let startX = 0;
      let isDown = false;
      let width = 0;

      const onMove = (e) => {
        if (!isDown) return;
        this.dragDelta = e.clientX - startX;
        // Live drag-follow (slide mode only; fade uses flick behaviour)
        if (!this.isFade) {
          const base = -this.index * width;
          this.track.style.transform = `translateX(${base + this.dragDelta}px)`;
        }
      };

      const onUp = (e) => {
        if (!isDown) return;
        isDown = false;
        this.classList.remove('is-dragging');
        this.dragDelta = e.clientX - startX;

        const threshold = Math.min(80, width * 0.15);
        if (Math.abs(this.dragDelta) > threshold) {
          if (this.dragDelta < 0) this.next(true);
          else this.prev(true);
        } else {
          // Didn't drag far enough — snap back to current slide.
          // Force it through even if a lock was set, since the CSS
          // is-dragging class already killed any in-flight transition.
          this.isAnimating = false;
          clearTimeout(this.navSafetyTimer);
          this.goTo(this.index, true);
        }
      };

      this.track.addEventListener('pointerdown', (e) => {
        if (e.pointerType === 'mouse' && e.button !== 0) return;

        // Grabbing the track physically overrides any programmatic
        // transition in flight (CSS sets transition:none while
        // .is-dragging is active), so any pending loop-reset
        // correction from a previous move may never get to run.
        // Force that correction NOW, before starting the new drag —
        // otherwise it's silently skipped and the internal position
        // keeps drifting past the single clone on each end with every
        // subsequent drag, eventually landing in empty space (blank/
        // white). This must happen before we read `width`/`startX`
        // below so the drag starts from the corrected position.
        clearTimeout(this.navSafetyTimer);
        if (this.isLooping) {
          if (this.index === 0) {
            this.setPositionInstant(this.total);
          } else if (this.index === this.total + 1) {
            this.setPositionInstant(1);
          }
        }
        this.isAnimating = false;

        isDown = true;
        startX = e.clientX;
        this.dragDelta = 0;
        width = this.track.clientWidth;
        this.classList.add('is-dragging');
        this.stopAutoplay();
      });

      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp);
      window.addEventListener('pointercancel', onUp);

      // Stop the browser's native image/link dragging from hijacking the gesture
      this.track.addEventListener('dragstart', (e) => e.preventDefault());

      // If the pointer moved (swipe), don't let the release count as a
      // click on the full-slide banner link or buttons.
      this.addEventListener(
        'click',
        (e) => {
          if (Math.abs(this.dragDelta) > 10) {
            e.preventDefault();
            e.stopPropagation();
            this.dragDelta = 0;
          }
        },
        true
      );
    }

    /* ---------- Autoplay ---------- */

    startAutoplay() {
      if (!this.autoplayEnabled || this.paused) return;
      this.stopAutoplay();
      this.timer = setInterval(() => this.next(), this.speed);
    }

    stopAutoplay() {
      if (this.timer) {
        clearInterval(this.timer);
        this.timer = null;
      }
    }

    restartAutoplay() {
      if (!this.autoplayEnabled) return;
      this.stopAutoplay();
      this.startAutoplay();
    }

    bindAutoplayPausing() {
      // Pause on hover (desktop)
      this.addEventListener('mouseenter', () => this.stopAutoplay());
      this.addEventListener('mouseleave', () => this.startAutoplay());

      // Pause when tab is hidden
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) this.stopAutoplay();
        else this.startAutoplay();
      });
    }

    /* ---------- Shopify theme editor ---------- */

    bindEditorEvents() {
      if (!window.Shopify || !window.Shopify.designMode) return;

      document.addEventListener('shopify:block:select', (e) => {
        const slide = e.target.closest ? e.target.closest('[data-slide]') : null;
        const target = slide || (e.target.querySelector && e.target.querySelector('[data-slide]'));
        // Ignore clones — the editor only ever targets real blocks.
        if (target && this.contains(target) && !target.hasAttribute('data-clone')) {
          this.paused = true;
          this.stopAutoplay();
          this.isAnimating = false;
          clearTimeout(this.navSafetyTimer);
          const realIndex = parseInt(target.dataset.index, 10);
          this.setPositionInstant(this.toInternalIndex(realIndex));
        }
      });

      document.addEventListener('shopify:block:deselect', (e) => {
        if (this.contains(e.target)) {
          this.paused = false;
          this.startAutoplay();
        }
      });
    }
  }

  customElements.define('hero-slideshow', HeroSlideshow);
}